import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { getSupabaseClient, Profile } from '@parentpath/supabase-client';

const supabase = getSupabaseClient();

// Refresh session 5 minutes before expiry
const REFRESH_MARGIN_SECONDS = 5 * 60;
// Check session every 60 seconds for visibility changes
const SESSION_CHECK_INTERVAL = 60 * 1000;

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initializedRef = useRef(false);
  const profileFetchedRef = useRef(false);

  const isAdmin = profile?.role === 'admin';

  const clearSafetyTimeout = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  // Clear all auth state and redirect to login
  const clearAuthState = useCallback((redirect = false) => {
    setUser(null);
    setProfile(null);
    setSession(null);
    setIsLoading(false);
    profileFetchedRef.current = false;
    clearSafetyTimeout();

    // Clear any pending refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Redirect to login if requested and not already there
    if (redirect && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, [clearSafetyTimeout]);

  // Schedule proactive token refresh before expiry
  const scheduleTokenRefresh = useCallback((currentSession: Session) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!currentSession.expires_at) return;

    const expiresAt = currentSession.expires_at * 1000; // Convert to ms
    const now = Date.now();
    const refreshAt = expiresAt - (REFRESH_MARGIN_SECONDS * 1000);
    const timeUntilRefresh = refreshAt - now;

    if (timeUntilRefresh <= 0) {
      // Token is already close to expiry or expired, refresh immediately
      console.log('Token close to expiry, refreshing now');
      supabase.auth.refreshSession().catch((err) => {
        console.error('Failed to refresh session:', err);
        clearAuthState(true);
      });
    } else {
      // Schedule refresh
      console.log(`Scheduling token refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
      refreshTimerRef.current = setTimeout(async () => {
        console.log('Proactively refreshing token');
        const { error } = await supabase.auth.refreshSession();
        if (error) {
          console.error('Proactive token refresh failed:', error);
          clearAuthState(true);
        }
      }, timeUntilRefresh);
    }
  }, [clearAuthState]);

  // Check session validity (called on visibility change and periodically)
  const checkSession = useCallback(async () => {
    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();

      if (error || !currentSession) {
        console.warn('Session check failed or no session');
        clearAuthState(true);
        return;
      }

      // If session is close to expiry, refresh it
      if (currentSession.expires_at) {
        const expiresAt = currentSession.expires_at * 1000;
        const now = Date.now();
        if (expiresAt - now < REFRESH_MARGIN_SECONDS * 1000) {
          console.log('Session close to expiry during check, refreshing');
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error('Session refresh failed:', refreshError);
            clearAuthState(true);
          }
        }
      }
    } catch (err) {
      console.error('Error checking session:', err);
    }
  }, [clearAuthState]);

  async function fetchProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // If we get a 401 or auth error, clear the session
        if (error.message?.includes('JWT') || error.code === 'PGRST301') {
          console.warn('Auth error fetching profile, signing out');
          await supabase.auth.signOut();
          clearAuthState();
          return;
        }
        throw error;
      }
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      clearSafetyTimeout();
      setIsLoading(false);
    }
  }

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Safety timeout - ensure loading state is cleared even if something goes wrong.
    // Stored in a ref so it can be cleared from onAuthStateChange and fetchProfile.
    safetyTimeoutRef.current = setTimeout(() => {
      console.warn('Auth safety timeout triggered - forcing loading state to false');
      safetyTimeoutRef.current = null;
      setIsLoading(false);
    }, 10000); // 10 second timeout

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session: initialSession }, error }) => {
      if (error) {
        console.error('Error getting session:', error);
        clearAuthState();
        return;
      }

      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      if (initialSession?.user) {
        // Mark as fetching to prevent duplicate fetch from onAuthStateChange
        if (!profileFetchedRef.current) {
          profileFetchedRef.current = true;
          await fetchProfile(initialSession.user.id);
        }
        // If profile was already fetched by onAuthStateChange (INITIAL_SESSION),
        // fetchProfile's finally block will clear the safety timeout. No action needed.
        scheduleTokenRefresh(initialSession);
      } else {
        clearSafetyTimeout();
        setIsLoading(false);
      }
    }).catch((err) => {
      console.error('Unexpected error in getSession:', err);
      clearAuthState();
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, newSession) => {
        console.log('Auth event:', event);

        // Handle token refresh failure
        if (event === 'TOKEN_REFRESHED' && !newSession) {
          console.warn('Token refresh failed, signing out');
          clearAuthState(true);
          return;
        }

        // Handle sign out or session expiry
        if (event === 'SIGNED_OUT' || !newSession) {
          clearAuthState();
          return;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Schedule next token refresh
        scheduleTokenRefresh(newSession);

        if (newSession?.user) {
          // Only fetch profile if not already fetched (prevents race condition)
          if (!profileFetchedRef.current) {
            profileFetchedRef.current = true;
            // Ensure loading state is true while profile is being fetched.
            // Without this, a SIGNED_IN after a null INITIAL_SESSION would leave
            // isLoading=false, causing ProtectedRoute to flash "Kein Zugriff"
            // before the profile has loaded.
            setIsLoading(true);
            await fetchProfile(newSession.user.id);
          } else {
            // Profile already fetched, just ensure loading is false
            setIsLoading(false);
          }
        } else {
          setProfile(null);
          setIsLoading(false);
        }
      }
    );

    // Check session when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && session) {
        console.log('Tab became visible, checking session');
        checkSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic session check
    sessionCheckIntervalRef.current = setInterval(() => {
      if (session) {
        checkSession();
      }
    }, SESSION_CHECK_INTERVAL);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearSafetyTimeout();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - refs handle state tracking

  async function signIn(email: string, password: string) {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function signOut() {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      clearAuthState();
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        isLoading,
        isAdmin,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
